import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ShareDialog from "@/components/ShareDialog";
import { toast } from "sonner";
import { Link2, Plus, Loader2, Trash2, Eye, Copy, ExternalLink, MapPin, Zap, CalendarDays } from "lucide-react";

const typeIcons = { VENUE: MapPin, EVENT: Zap, BOOKING_PRESET: CalendarDays };
const typeLabels = { VENUE: "Venue", EVENT: "Event", BOOKING_PRESET: "Booking" };
const typeColors = { VENUE: "bg-blue-100 text-blue-800", EVENT: "bg-purple-100 text-purple-800", BOOKING_PRESET: "bg-amber-100 text-amber-800" };

export default function ProviderShareLinks() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [shareDialog, setShareDialog] = useState(null); // {url, title, description}
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    link_type: "VENUE", title: "", description: "",
    venue_id: "", resource_id: "", event_id: "",
    preset_date: "", preset_time: "", sport_type: "",
  });

  const fetchLinks = async () => {
    try { const res = await api.get("/share-links/my"); setLinks(res.data); } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchLinks();
    api.get("/provider/venues").then(r => setVenues(r.data)).catch(() => {});
    api.get("/provider/events").then(r => setEvents(r.data)).catch(() => {});
  }, []);

  const allResources = venues.flatMap(v => (v.resources || []).map(r => ({ ...r, venue_name: v.name })));

  const handleCreate = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    if (form.link_type === "VENUE" && !form.venue_id) { toast.error("Select a venue"); return; }
    if (form.link_type === "EVENT" && !form.event_id) { toast.error("Select an event"); return; }
    if (form.link_type === "BOOKING_PRESET" && !form.venue_id) { toast.error("Select a venue"); return; }
    setSaving(true);
    try {
      const res = await api.post("/share-links", form);
      toast.success("Share link created!");
      const fullUrl = `${window.location.origin}/s/${res.data.code}`;
      setShareDialog({ url: fullUrl, title: form.title, description: form.description });
      setShowForm(false);
      setForm({ link_type: "VENUE", title: "", description: "", venue_id: "", resource_id: "", event_id: "", preset_date: "", preset_time: "", sport_type: "" });
      fetchLinks();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDelete = async (linkId) => {
    try {
      await api.delete(`/share-links/${linkId}`);
      toast.success("Link deleted");
      fetchLinks();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/s/${code}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied!")).catch(() => {});
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="share-links-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Link2 className="w-8 h-8 text-court-orange" />Share Links
          </h1>
          <p className="text-muted-foreground mt-1">Create links to share with your players and groups</p>
        </div>
        <Button data-testid="create-share-link-btn" onClick={() => setShowForm(true)}
          className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide px-6 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
          <Plus className="w-4 h-4 mr-2" />Create Link
        </Button>
      </div>

      {links.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Link2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No share links yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create shareable links for your venues, events, or pre-filled bookings</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map(link => {
            const Icon = typeIcons[link.link_type] || Link2;
            const fullUrl = `${window.location.origin}/s/${link.code}`;
            return (
              <div key={link.link_id} className="bg-white border-2 border-slate-100 p-4" data-testid={`share-link-${link.link_id}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Icon className="w-4 h-4 text-court-orange shrink-0" />
                      <h3 className="font-heading text-base font-bold uppercase tracking-tight">{link.title}</h3>
                      <Badge className={`${typeColors[link.link_type]} text-[10px] font-heading uppercase`}>{typeLabels[link.link_type]}</Badge>
                    </div>
                    {link.description && <p className="text-sm text-muted-foreground mb-1">{link.description}</p>}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="font-mono text-court-orange truncate max-w-[200px]">/s/{link.code}</span>
                      {link.venue_name && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{link.venue_name}</span>}
                      {link.preset_date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{link.preset_date}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-center px-3">
                      <p className="font-heading text-lg font-bold">{link.views}</p>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" />views</p>
                    </div>
                    <Button data-testid={`copy-${link.code}`} size="sm" variant="outline" onClick={() => copyLink(link.code)}
                      className="rounded-full border-2 font-heading uppercase text-[10px] px-3 h-8">
                      <Copy className="w-3 h-3 mr-1" />Copy
                    </Button>
                    <Button data-testid={`share-${link.code}`} size="sm" onClick={() => setShareDialog({ url: fullUrl, title: link.title, description: link.description })}
                      className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-[10px] px-3 h-8">
                      <ExternalLink className="w-3 h-3 mr-1" />Share
                    </Button>
                    <Button data-testid={`delete-${link.link_id}`} size="sm" variant="outline" onClick={() => handleDelete(link.link_id)}
                      className="rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 h-8 w-8 p-0">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Link Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-link-dialog">
          <DialogHeader><DialogTitle className="font-heading uppercase">Create Share Link</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-heading uppercase">Link Type</Label>
              <div className="flex gap-2 mt-1">
                {[
                  { value: "VENUE", label: "Venue", icon: MapPin },
                  { value: "EVENT", label: "Event", icon: Zap },
                  { value: "BOOKING_PRESET", label: "Pre-filled Booking", icon: CalendarDays },
                ].map(t => (
                  <button key={t.value} data-testid={`type-${t.value.toLowerCase()}`}
                    onClick={() => setForm({ ...form, link_type: t.value })}
                    className={`flex-1 flex items-center gap-2 justify-center py-2.5 border-2 text-xs font-heading uppercase transition-colors
                      ${form.link_type === t.value ? 'border-court-orange bg-court-orange/5 text-court-orange' : 'border-slate-200 text-slate-500'}`}>
                    <t.icon className="w-3.5 h-3.5" />{t.label}
                  </button>
                ))}
              </div>
            </div>

            <div><Label className="text-xs font-heading uppercase">Title</Label>
              <Input data-testid="link-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Book our basketball court" className="h-11 rounded-none border-2 mt-1" /></div>

            <div><Label className="text-xs font-heading uppercase">Description (optional)</Label>
              <Input data-testid="link-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Share this with your teammates!" className="h-11 rounded-none border-2 mt-1" /></div>

            {(form.link_type === "VENUE" || form.link_type === "BOOKING_PRESET") && (
              <div><Label className="text-xs font-heading uppercase">Venue</Label>
                <Select value={form.venue_id} onValueChange={v => setForm({ ...form, venue_id: v })}>
                  <SelectTrigger data-testid="link-venue-select" className="h-11 rounded-none border-2 mt-1"><SelectValue placeholder="Select venue" /></SelectTrigger>
                  <SelectContent>{venues.map(v => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name} - {v.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {form.link_type === "BOOKING_PRESET" && (
              <>
                {form.venue_id && (
                  <div><Label className="text-xs font-heading uppercase">Court (optional)</Label>
                    <Select value={form.resource_id} onValueChange={v => setForm({ ...form, resource_id: v })}>
                      <SelectTrigger data-testid="link-resource-select" className="h-11 rounded-none border-2 mt-1"><SelectValue placeholder="Any court" /></SelectTrigger>
                      <SelectContent>
                        {allResources.filter(r => venues.find(v => v.venue_id === form.venue_id)?.resources?.some(vr => vr.resource_id === r.resource_id)).map(r =>
                          <SelectItem key={r.resource_id} value={r.resource_id}>{r.name} ({r.sport_type})</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs font-heading uppercase">Date (optional)</Label>
                    <Input data-testid="link-date-input" type="date" value={form.preset_date} onChange={e => setForm({ ...form, preset_date: e.target.value })} className="h-11 rounded-none border-2 mt-1" /></div>
                  <div><Label className="text-xs font-heading uppercase">Time (optional)</Label>
                    <Input data-testid="link-time-input" type="time" value={form.preset_time} onChange={e => setForm({ ...form, preset_time: e.target.value })} className="h-11 rounded-none border-2 mt-1" /></div>
                </div>
              </>
            )}

            {form.link_type === "EVENT" && (
              <div><Label className="text-xs font-heading uppercase">Event</Label>
                <Select value={form.event_id} onValueChange={v => setForm({ ...form, event_id: v })}>
                  <SelectTrigger data-testid="link-event-select" className="h-11 rounded-none border-2 mt-1"><SelectValue placeholder="Select event" /></SelectTrigger>
                  <SelectContent>{events.filter(e => e.status === "ACTIVE").map(e =>
                    <SelectItem key={e.event_id} value={e.event_id}>{e.title} - {e.event_date}</SelectItem>
                  )}</SelectContent>
                </Select>
              </div>
            )}

            <Button data-testid="save-link-btn" onClick={handleCreate} disabled={saving}
              className="w-full bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase h-12 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
              {saving ? "Creating..." : "Create Share Link"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      {shareDialog && (
        <ShareDialog open={!!shareDialog} onOpenChange={() => setShareDialog(null)}
          shareUrl={shareDialog.url} title={shareDialog.title} description={shareDialog.description} />
      )}
    </div>
  );
}
